import AssignedTab from "@/components/assignWizard/AssignedTab";
import AssignModalTwo from "@/components/assignWizard/AssignModal";
import Button from "@/components/button/Button";
import BuildingColumns from "@/models/ColumnDefinitions/BuildingColumns";
import { DeviceImproxColumns } from "@/models/ColumnDefinitions/DeviceImproxColumns";
import DoorColumns from "@/models/ColumnDefinitions/DoorColumns";
import SiteColumns from "@/models/ColumnDefinitions/SiteColumns";
import ZoneColumns from "@/models/ColumnDefinitions/ZoneColumns";
import { ListData } from "@/models/ListData";
import { ListQuery } from "@/models/ListQuery";
import { Lookup } from "@/models/Lookup";
import LookupTypes from "@/models/LookupTypes";
import { PaginationModel } from "@/models/Pagination";
import BuildingSearch from "@/models/search/BuildingSearch";
import ControllerSearch from "@/models/search/ControllerSearch";
import DeviceImproxSearch from "@/models/search/DeviceImproxSearch";
import DoorSearch from "@/models/search/DoorSearch";
import { SearchTerm } from "@/models/search/SearchTerm";
import SiteSearch from "@/models/search/SiteSearch";
import TimeZoneSearch from "@/models/search/TimeZoneSearch";
import ZoneSearch from "@/models/search/ZoneSearch";
import { TableColumn } from "@/models/table/TableColumn";
import { getBuildingNamesWithPagination, getBuildings } from "@/services/buildingService";
import { getControllerNames } from "@/services/controllerService";
import { getDeviceImprox, getDeviceImproxNamesWithPagnation } from "@/services/deviceImproxService";
import { getDoorNamesWithPagination, getDoors } from "@/services/doorService";
import { getSiteNamesWithPagination, getSites } from "@/services/siteService";
import { getTimeZones } from "@/services/timeZoneService";
import { getErrorMessage } from "@/services/utilities/ServiceError";
import { getZoneNamesWithPagination, getZones } from "@/services/zoneService";
import { appendFilter } from "@/utilities/filters";
import { useFormikContext } from "formik";
import React, { FC, ReactElement, useState } from "react";
import { toast } from "react-toastify";
import { FILTER_BUILDINGS, FILTER_DEVICE_TYPE, FILTER_DOORS, FILTER_SITES, FILTER_ZONES } from "../../../constants/notification-key";
import { useLocale } from "../../../contexts/localeContext";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { SettingData } from "../../../models/SettingData";
import { handleManage } from "../../../services/notificationService";
import ActivityFrame from "../../activity-frame/activity-frame";

interface EventBasedDetailsProps {
    reloadPage: () => void;
}

const EventBasedDetails: FC<EventBasedDetailsProps> = ({ reloadPage }) => {
    const [busy, setBusy] = useState(false);
    const { values } = useFormikContext<any>();
    const { translate } = useLocale();
    const [showAssignModal, setShowAssignModal] = useState<boolean>(false);
    type filterByProps = {
        tabTitle: string;
        tableColumn: TableColumn[];
        searchColumn: SearchTerm[];
        tabDataMethod: (query?: ListQuery | undefined) => Promise<ListData>;
        modalDataMethod: (query?: ListQuery | undefined) => Promise<ListData>;
        modalTitle: string;
        assigningTitle: string;
        lookups?: Lookup[];
    }

    const getContent = (filterBy: string): ReactElement => {
        let filterByInfo: filterByProps;
        const emptyResponseFunction = (listParameters?: ListQuery) => Promise.resolve({ rows: [], pagination: { maxPageSize: 0, pageNumber: 1, totalCount: 0, totalPages: 0 } });

        if (filterBy === FILTER_SITES.eSysnameLookup) {
            filterByInfo = {
                tableColumn: SiteColumns,
                searchColumn: SiteSearch,
                tabTitle: translate.byKey(TranslationKeys.sites),
                tabDataMethod: (listParameters?: ListQuery) => getSites({ ...listParameters, filters: appendFilter(values.filterIds?.toString(), "Items", "id", listParameters?.filters ?? [], "=in=") }),
                modalDataMethod: getSites,
                modalTitle: translate.byKey(TranslationKeys.assignSites),
                assigningTitle: translate.byKey(TranslationKeys.site),
                lookups: [
                    {
                        name: LookupTypes.timeZone,
                        values: [],
                        dataMethod: getTimeZones,
                        searchAccessor: TimeZoneSearch.find(item => item.accessor === "name")?.searchOnName
                    }
                ]

            };
        }
        else if (filterBy === FILTER_DOORS.eSysnameLookup) {
            filterByInfo = {
                tableColumn: DoorColumns,
                searchColumn: DoorSearch,
                tabTitle: translate.byKey(TranslationKeys.doors),
                tabDataMethod: (listParameters?: ListQuery) => getDoors({ ...listParameters, filters: appendFilter(values.filterIds?.toString(), "Items", "id", listParameters?.filters ?? [], "=in=") }),
                modalDataMethod: getDoors,
                modalTitle: translate.byKey(TranslationKeys.assignDoors),
                assigningTitle: translate.byKey(TranslationKeys.door),
                lookups: [
                    {
                        name: LookupTypes.zoneName,
                        values: [],
                        dataMethod: getZoneNamesWithPagination,
                        searchAccessor: ZoneSearch.find(item => item.accessor === "name")?.searchOnName
                    },
                    {
                        name: LookupTypes.zoneController,
                        values: [],
                        dataMethod: getControllerNames,
                        searchAccessor: ControllerSearch.find(item => item.accessor === "name")?.searchOnName
                    },
                    {
                        name: LookupTypes.zoneSite,
                        values: [],
                        dataMethod: getSiteNamesWithPagination,
                        searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
                    }
                ]
            };
        }
        else if (filterBy === FILTER_BUILDINGS.eSysnameLookup) {
            filterByInfo = {
                tableColumn: BuildingColumns,
                searchColumn: BuildingSearch,
                tabTitle: translate.byKey(TranslationKeys.buildings),
                tabDataMethod: (listParameters?: ListQuery) => getBuildings({ ...listParameters, filters: appendFilter(values.filterIds?.toString(), "Items", "id", listParameters?.filters ?? [], "=in=") }),
                modalDataMethod: getBuildings,
                modalTitle: translate.byKey(TranslationKeys.assignBuildings),
                assigningTitle: translate.byKey(TranslationKeys.building),
                lookups: [
                    {
                        name: LookupTypes.site,
                        values: [],
                        dataMethod: getSiteNamesWithPagination,
                        searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
                    }
                ]
            };
        }
        else if (filterBy === FILTER_ZONES.eSysnameLookup) {
            filterByInfo = {
                tableColumn: ZoneColumns,
                searchColumn: ZoneSearch,
                tabTitle: translate.byKey(TranslationKeys.zones),
                tabDataMethod: (listParameters?: ListQuery) => getZones({ ...listParameters, filters: appendFilter(values.filterIds?.toString(), "Items", "id", listParameters?.filters ?? [], "=in=") }),
                modalDataMethod: getZones,
                modalTitle: translate.byKey(TranslationKeys.assignZones),
                assigningTitle: translate.byKey(TranslationKeys.zone),
                lookups: [
                    {
                        name: LookupTypes.zoneBySiteId,
                        values: [],
                        dataMethod: getSiteNamesWithPagination,
                        searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
                    },
                    {
                        name: "controller",
                        values: [],
                        dataMethod: getControllerNames,
                        searchAccessor: ControllerSearch.find(item => item.accessor === "name")?.searchOnName
                    }
                ]
            };
        }
        else if (filterBy === FILTER_DEVICE_TYPE.eSysnameLookup) {
            filterByInfo = {
                tableColumn: DeviceImproxColumns,
                searchColumn: DeviceImproxSearch,
                tabTitle: translate.byKey(TranslationKeys.devices),
                tabDataMethod: (listParameters?: ListQuery) => getDeviceImprox({ ...listParameters, filters: appendFilter(values.filterIds?.toString(), "Items", "id", listParameters?.filters ?? [], "=in=") }),
                modalDataMethod: getDeviceImprox,
                modalTitle: translate.byKey(TranslationKeys.assignDevices),
                assigningTitle: translate.byKey(TranslationKeys.device)
            };
        }
        else {
            return <>{translate.byKey(TranslationKeys.notFound)}</>;
        }

        return <>
            <AssignedTab
                column={filterByInfo.tableColumn}
                searchColumn={filterByInfo.searchColumn}
                id={values.filterIds?.toString()}
                key={`${filterBy}-tab`}
                title={filterByInfo.tabTitle}
                dataMethod={values.filterIds && values.filterIds.length > 0 ? filterByInfo.tabDataMethod : emptyResponseFunction}>
                <Button variant="submit" shape="rounded" className="py-1 px-5" onClick={() => setShowAssignModal(true)} key={`${filterBy}-button`} data-testid="manage-button">
                    {translate.byKey(TranslationKeys.manage)}
                </Button>
            </AssignedTab>
            <AssignModalTwo
                key={`${filterBy}-modal`}
                isOpen={showAssignModal}
                dataMethod={filterByInfo.modalDataMethod}
                lookup={filterByInfo.lookups}
                onClose={() => setShowAssignModal(false)}
                modalTitle={filterByInfo.modalTitle}
                tableColumns={filterByInfo.tableColumn}
                searchColumns={filterByInfo.searchColumn}
                assigningTitle={filterByInfo.assigningTitle}
                getAssignedItems={getAssignedItems}
                handleSubmit={handleSubmit} />
        </>
    }

    const buildSettingDataToAdd = (toBeAdded: string[]): SettingData[] => {
        const settingDataToAdd: SettingData[] = toBeAdded.map((id) => ({
            sdValstring: id,
            eSysnameLookup: values.filterBy,
            relationship: [
                {
                    id: values.vruleFilterIds,
                    type: "ruleTriggerGroup",
                    relationType: "vruleTriggerGroup"
                },
                {
                    id: id,
                    type: values.relationType
                }
            ]

        }))

        return settingDataToAdd;
    }

    const getSettingDataIdsToDelete = (toBeRemoved: string[]): string[] => {
        let settingDataToRemove: string[] = [];

        if (toBeRemoved.length > 0) {
            const filteredOutSettingData = values.settingDatasRef.filter((ref) => ref.elementSysname === values.filterBy && toBeRemoved.includes(ref.sdValstring));
            filteredOutSettingData.map((remove) => {
                settingDataToRemove.push(remove.id)
            })
        }

        return settingDataToRemove;
    }

    const handleSubmit = (toBeAdded: string[], toBeRemoved: string[]) => {
        setBusy(true);
        const dataToAdd = buildSettingDataToAdd(toBeAdded);
        const dataToRemove = getSettingDataIdsToDelete(toBeRemoved);

        handleManage(dataToAdd, dataToRemove).then((response: any) => {
            toast.success(translate.byKey(TranslationKeys.success));
            reloadPage();
        }).catch((message) => {
            toast.error(getErrorMessage(message));
        }).finally(() => {
            setBusy(false);
        })
    }

    const getAssignedItemsDataMethod = (listParameters?: ListQuery) => {
        const filter = appendFilter(values.filterIds?.toString(), "Items", "id", listParameters?.filters ?? [], "=in=");
        const filterBy = values.filterIds && values.filterIds.length > 0 ? values.filterBy : "";

        switch (filterBy) {
            case FILTER_SITES.eSysnameLookup:
                return getSiteNamesWithPagination({ ...listParameters, filters: filter });
            case FILTER_BUILDINGS.eSysnameLookup:
                return getBuildingNamesWithPagination({ ...listParameters, filters: filter });
            case FILTER_ZONES.eSysnameLookup:
                return getZoneNamesWithPagination({ ...listParameters, filters: filter });
            case FILTER_DOORS.eSysnameLookup:
                return getDoorNamesWithPagination({ ...listParameters, filters: filter });
            case FILTER_DEVICE_TYPE.eSysnameLookup:
                return getDeviceImproxNamesWithPagnation({ ...listParameters, filters: filter });
            default:
                return Promise.resolve({ rows: [], pagination: { maxPageSize: 0, pageNumber: 1, totalCount: 0, totalPages: 0 } });
        }
    }

    const getAssignedItems = async (setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setSelectedRows: React.Dispatch<React.SetStateAction<any[]>>, setOriginalSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setOriginalSelectedRows: React.Dispatch<React.SetStateAction<any[]>>, setBusyLoader: React.Dispatch<React.SetStateAction<boolean>>) => {
        try {
            setBusyLoader(true);
            let pageNumber = 1, pagination: PaginationModel, ids: string[] = [], rows: any[] = []

            do {
                const data = await getAssignedItemsDataMethod({ goToPage: pageNumber, pageSize: 100 });
                ids = ids.concat(data.rows.map(item => item.value))
                rows = rows.concat(data.rows.map(item => ({ id: item.value, name: item.label })));
                pagination = data.pagination;
                pageNumber++;

            } while (pageNumber <= pagination.totalPages);

            setSelectedIds(ids);
            setSelectedRows(rows)
            setOriginalSelectedIds(ids);
            setOriginalSelectedRows(rows);
        } catch (error) {
            toast.warn(getErrorMessage(error));
        }
        finally {
            setBusyLoader(false);
        }
    }

    return (
        <ActivityFrame busy={busy} className='flex flex-col flex-grow bg-white'>
            {getContent(values.filterBy)}
        </ActivityFrame>
    )
}

export default EventBasedDetails;
